import React, { useEffect, useMemo, useRef, useState } from "react";

type Props = {
  onPick: (emoji: string) => void;
  onClose: () => void;
  className?: string;
};

const CATEGORIES: { name: string; emojis: { char: string; keywords: string }[] }[] = [
  {
    name: "Smileys & People",
    emojis: [
      { char: "😀", keywords: "grin smile happy" },
      { char: "😃", keywords: "smile happy joy" },
      { char: "😄", keywords: "smile happy laugh" },
      { char: "😁", keywords: "grin beam" },
      { char: "😆", keywords: "laugh squint" },
      { char: "😅", keywords: "sweat laugh" },
      { char: "🤣", keywords: "rofl laugh" },
      { char: "😂", keywords: "joy tears laugh" },
      { char: "🙂", keywords: "slight smile" },
      { char: "🙃", keywords: "upside down" },
      { char: "😉", keywords: "wink" },
      { char: "😊", keywords: "blush smile" },
      { char: "😇", keywords: "angel halo" },
      { char: "🥰", keywords: "love hearts" },
      { char: "😍", keywords: "heart eyes love" },
      { char: "🤩", keywords: "star eyes wow" },
      { char: "😘", keywords: "kiss" },
      { char: "😗", keywords: "kiss" },
      { char: "😜", keywords: "wink tongue" },
      { char: "🤪", keywords: "zany crazy" },
      { char: "🤗", keywords: "hug" },
      { char: "🤔", keywords: "thinking" },
      { char: "🤨", keywords: "eyebrow suspicious" },
      { char: "😐", keywords: "neutral" },
      { char: "😑", keywords: "expressionless" },
      { char: "😶", keywords: "no mouth silent" },
      { char: "😏", keywords: "smirk" },
      { char: "😒", keywords: "unamused meh" },
      { char: "🙄", keywords: "roll eyes" },
      { char: "😬", keywords: "grimace" },
      { char: "🤥", keywords: "lying" },
      { char: "😌", keywords: "relieved calm" },
      { char: "😔", keywords: "pensive sad" },
      { char: "😪", keywords: "sleepy" },
      { char: "😴", keywords: "sleep" },
      { char: "🤤", keywords: "drool" },
      { char: "😷", keywords: "mask sick" },
      { char: "🤒", keywords: "sick fever" },
      { char: "🤕", keywords: "hurt bandage" },
      { char: "🥵", keywords: "hot sweat" },
      { char: "🥶", keywords: "cold freeze" },
      { char: "😎", keywords: "cool sunglasses" },
      { char: "🤓", keywords: "nerd" },
      { char: "🧐", keywords: "monocle" },
      { char: "😕", keywords: "confused" },
      { char: "😟", keywords: "worried" },
      { char: "😢", keywords: "cry sad tear" },
      { char: "😭", keywords: "sob cry" },
      { char: "😤", keywords: "triumph huff" },
      { char: "😡", keywords: "angry mad" },
      { char: "🤬", keywords: "curse swear" },
      { char: "🥺", keywords: "pleading beg" },
      { char: "🤯", keywords: "mind blown" },
      { char: "🤠", keywords: "cowboy" },
      { char: "🥳", keywords: "party celebrate" },
      { char: "😱", keywords: "scream shock" },
    ],
  },
  {
    name: "Gestures & Body",
    emojis: [
      { char: "👍", keywords: "thumbs up like approve" },
      { char: "👎", keywords: "thumbs down dislike" },
      { char: "👌", keywords: "ok perfect" },
      { char: "✌️", keywords: "peace victory" },
      { char: "🤞", keywords: "crossed fingers luck" },
      { char: "🤟", keywords: "love you" },
      { char: "🤘", keywords: "rock horns" },
      { char: "🤙", keywords: "call shaka" },
      { char: "👈", keywords: "point left" },
      { char: "👉", keywords: "point right" },
      { char: "👆", keywords: "point up" },
      { char: "👇", keywords: "point down" },
      { char: "☝️", keywords: "index up one" },
      { char: "👋", keywords: "wave hi bye" },
      { char: "🤚", keywords: "raised back hand" },
      { char: "🖐️", keywords: "hand splayed" },
      { char: "✋", keywords: "stop hand" },
      { char: "🖖", keywords: "vulcan spock" },
      { char: "👏", keywords: "clap applause" },
      { char: "🙌", keywords: "raised hands celebrate" },
      { char: "🙏", keywords: "pray thanks" },
      { char: "🤝", keywords: "handshake deal" },
      { char: "💪", keywords: "muscle strong" },
      { char: "🫶", keywords: "heart hands love" },
    ],
  },
  {
    name: "Hearts & Symbols",
    emojis: [
      { char: "❤️", keywords: "heart love red" },
      { char: "🧡", keywords: "heart orange" },
      { char: "💛", keywords: "heart yellow" },
      { char: "💚", keywords: "heart green" },
      { char: "💙", keywords: "heart blue" },
      { char: "💜", keywords: "heart purple" },
      { char: "🖤", keywords: "heart black" },
      { char: "🤍", keywords: "heart white" },
      { char: "🤎", keywords: "heart brown" },
      { char: "💔", keywords: "broken heart" },
      { char: "❣️", keywords: "heart exclamation" },
      { char: "💕", keywords: "two hearts love" },
      { char: "💖", keywords: "sparkling heart" },
      { char: "💗", keywords: "growing heart" },
      { char: "💘", keywords: "heart arrow" },
      { char: "💝", keywords: "heart gift" },
      { char: "💟", keywords: "heart decoration" },
      { char: "⭐", keywords: "star" },
      { char: "🌟", keywords: "glowing star" },
      { char: "✨", keywords: "sparkles" },
      { char: "⚡", keywords: "lightning" },
      { char: "🔥", keywords: "fire hot" },
      { char: "💥", keywords: "boom explode" },
      { char: "💯", keywords: "100 perfect" },
      { char: "✅", keywords: "check yes" },
      { char: "❌", keywords: "x wrong no" },
      { char: "❓", keywords: "question" },
      { char: "❗", keywords: "exclamation" },
      { char: "⚠️", keywords: "warning" },
    ],
  },
  {
    name: "Objects & Misc",
    emojis: [
      { char: "🎉", keywords: "party popper celebrate" },
      { char: "🎊", keywords: "confetti" },
      { char: "🎂", keywords: "cake birthday" },
      { char: "🎁", keywords: "gift present" },
      { char: "🏆", keywords: "trophy win" },
      { char: "🥇", keywords: "gold first" },
      { char: "🚀", keywords: "rocket launch" },
      { char: "💡", keywords: "idea bulb" },
      { char: "📌", keywords: "pin" },
      { char: "📎", keywords: "clip" },
      { char: "🔖", keywords: "bookmark" },
      { char: "🔔", keywords: "bell" },
      { char: "💬", keywords: "chat speech" },
      { char: "💭", keywords: "thought" },
      { char: "🗨️", keywords: "speech bubble" },
      { char: "👀", keywords: "eyes look" },
      { char: "👁️", keywords: "eye" },
    ],
  },
];

export const EmojiPicker: React.FC<Props> = ({ onPick, onClose, className = "" }) => {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return CATEGORIES;
    return CATEGORIES.map((cat) => ({
      ...cat,
      emojis: cat.emojis.filter((e) => e.keywords.toLowerCase().includes(q) || e.char.includes(q)),
    })).filter((cat) => cat.emojis.length > 0);
  }, [query]);

  return (
    <div
      className={`flex w-[280px] flex-col overflow-hidden rounded-lg border border-[#e2e2fc] bg-white shadow-[0_12px_32px_rgba(16,24,40,0.16)] ${className}`}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      <div className="border-b border-[#f4f4f7] p-2">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search"
          className="w-full rounded-md border border-[#e2e2fc] bg-[#fafafd] px-2 py-1 text-[12px] text-[#130f26] placeholder:text-[#130f26]/40 focus:border-[#4f5b94] focus:outline-none"
        />
      </div>
      <div className="max-h-[260px] overflow-y-auto px-2 pb-2" style={{ scrollbarWidth: "thin" }}>
        {filtered.length === 0 ? (
          <div className="py-6 text-center text-[12px] text-[#130f26]/50">No emoji found</div>
        ) : (
          filtered.map((cat) => (
            <div key={cat.name} className="pt-2">
              <div className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-[#130f26]/50">
                {cat.name}
              </div>
              <div className="grid grid-cols-8 gap-0.5">
                {cat.emojis.map((e) => (
                  <button
                    key={e.char}
                    type="button"
                    onClick={() => onPick(e.char)}
                    className="flex h-7 w-7 items-center justify-center rounded text-[18px] leading-none hover:bg-[#f4f4f7]"
                    title={e.keywords}
                  >
                    {e.char}
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default EmojiPicker;
